import { useMount } from '@apia/util';
import { usePanelContext } from '.';

/**
 * **Favor de leer con cuidado el siguiente contenido, es muy importante para
 * la performance**
 *
 * El hook useYieldPanelIsReady es una parte muy importante del flujo de
 * trabajo de los dashboards. **El no reportar la carga de un panel tiene
 * penalizaciones graves en el tiempo de renderizado inicial**, esto es porque
 * el dashboard espera que todos los paneles hayan terminado la carga
 * estructural básica antes de empezar con los refresh iniciales, y esto termina
 * impactando en el tiempo que demora en bajar la cortina.
 *
 * **Aclaración:** Actualmente se espera las cargas estructurales para lanzar
 * los refresh iniciales por motivos de arquitectura. Esa restricción será
 * eliminada probablemente en el corto plazo, pero eso no cambia la necesidad de
 * reportar las estructuras ya que esta información es igualmente utilizada para
 * el bajado de la cortina de Skeletons.
 *
 * Por ejemplo, si todos los paneles están optimizados para lograr cargas
 * iniciales rápidas pero el logo de la aplicación no reporta que su estructura
 * está pronta, el dashboard va a bajar su cortina solamente luego de que expire
 * el timeout **DASH_COURTAIN_TRESHOLD**, que por defecto viene establecido en 5
 * segundos.
 *
 * Esto significa que aún cuando la cortina se hubiera podido bajar quizás a
 * los pocos ms de cargado el html inicial, no lo hará porque supone que el
 * panel Logo aún no terminó su carga estructural.
 *
 * Siempre que un panel esté presente en un dashboard, deberá reportar cuando
 * la carga estructural esté pronta. Esto puede ser hecho de dos formas:
 *
 * - Usando { setRef } = usePanelContext(): En el elemento crucial de HTML del
 * elemento hacer ref={setRef}. Esta es la forma más óptima ya que nos garantiza
 * que el panel se reporte cuando su estructura HTML ya esté disponible,
 * evitando ninguna demora.
 *
 * - Usando useYieldPanelIsReady(): Este hook es una alternativa disponible
 * para las situaciones en las que no tenemos ningún elemento HTML de
 * importancia o cuando ese elemento no está disponible hasta después de la
 * carga inicial. Al usar este hook el panel se reportará automáticamente una
 * vez que haya hecho el renderizado inicial.
 */
export function useYieldPanelIsReady() {
  const { setRef } = usePanelContext();

  useMount(() => {
    setRef({} as HTMLElement);
  });
}
